import React, { useEffect, useMemo, useState } from "react";
import { X, Save, FilterX, Database, Columns3, Filter } from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { toast } from "sonner";

/**
 * CustomReportModal — create or edit a saved custom report definition.
 *
 * The modal is data-driven by `/api/reports/custom/fields` so adding a
 * new data source on the backend (a key in `CUSTOM_REPORT_SOURCES`)
 * automatically appears here without UI changes. The shape is wide
 * but deliberately shallow: source → window → filters → columns →
 * format. Each section is collapsible-by-grouping so the modal stays
 * scannable even with 13 columns and 4 filter fields.
 */
export default function CustomReportModal({ initial, onClose, onSaved }) {
    const [catalog, setCatalog] = useState(null);
    const [name, setName] = useState(initial?.name || "");
    const [description, setDescription] = useState(initial?.description || "");
    const [source, setSource] = useState(initial?.source || "jobs");
    const [windowDays, setWindowDays] = useState(initial?.window_days || 30);
    const [filters, setFilters] = useState(initial?.filters || {});
    const [columns, setColumns] = useState(initial?.columns || []);
    const [format, setFormat] = useState(initial?.format || "xlsx");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        http.get("/reports/custom/fields")
            .then((r) => setCatalog(r.data.sources))
            .catch((e) => setErr(errMsg(e)));
    }, []);

    const currentSource = useMemo(
        () => catalog?.find((s) => s.key === source) || null,
        [catalog, source],
    );

    // When the user switches data sources mid-edit, reset filters +
    // seed columns to the new source's defaults — otherwise we'd
    // submit `status: foo` for a source that has no `status` field.
    useEffect(() => {
        if (!currentSource) return;
        if (initial && initial.source === source) return; // editing — keep
        setColumns(currentSource.default_columns);
        setFilters({});
    }, [source, currentSource, initial]);

    // On fresh open (no initial), seed columns from the freshly loaded
    // catalog rather than leaving the picker empty.
    useEffect(() => {
        if (!initial && currentSource && columns.length === 0) {
            setColumns(currentSource.default_columns);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSource]);

    const toggleColumn = (key) => {
        setColumns((c) => (c.includes(key) ? c.filter((x) => x !== key) : [...c, key]));
    };

    const save = async () => {
        setErr("");
        if (!name.trim()) {
            setErr("Name is required");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: name.trim(),
                description: description.trim(),
                source,
                window_days: Number(windowDays),
                filters: Object.fromEntries(
                    Object.entries(filters).filter(([, v]) => v && v !== "any"),
                ),
                columns,
                format,
            };
            if (initial?.id) {
                await http.put(`/reports/custom/${initial.id}`, payload);
                toast.success(`Updated "${payload.name}"`);
            } else {
                await http.post("/reports/custom", payload);
                toast.success(`Created "${payload.name}"`);
            }
            onSaved?.();
            onClose?.();
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setSaving(false);
        }
    };

    if (!catalog) {
        return (
            <Backdrop onClose={onClose}>
                <div className="rounded-xl bg-surface p-6 text-sm text-muted-foreground">Loading…</div>
            </Backdrop>
        );
    }

    return (
        <Backdrop onClose={onClose}>
            <div
                className="relative max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-border bg-surface shadow-2xl"
                data-testid="custom-report-modal"
            >
                <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-surface px-6 py-4">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                            {initial ? "Edit custom report" : "New custom report"}
                        </div>
                        <h2 className="mt-0.5 font-display text-xl font-bold">
                            {initial?.name || "Untitled report"}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                        data-testid="custom-report-close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="space-y-5 p-6">
                    {/* Identity */}
                    <Section icon={Database} title="Identity">
                        <div className="grid gap-3 sm:grid-cols-3">
                            <Field label="Name" className="sm:col-span-2">
                                <input
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Monthly migration summary"
                                    data-testid="custom-report-name"
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Format">
                                <select
                                    value={format}
                                    onChange={(e) => setFormat(e.target.value)}
                                    data-testid="custom-report-format"
                                    className={inputCls}
                                >
                                    <option value="xlsx">XLSX (Excel)</option>
                                    <option value="pdf">PDF</option>
                                    <option value="csv">CSV</option>
                                </select>
                            </Field>
                        </div>
                        <Field label="Description (optional)">
                            <input
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Sent to the SecOps team monthly"
                                data-testid="custom-report-desc"
                                className={inputCls}
                            />
                        </Field>
                    </Section>

                    {/* Data source + window */}
                    <Section icon={Database} title="Data source">
                        <div className="grid gap-3 sm:grid-cols-3">
                            <Field label="Source" className="sm:col-span-2">
                                <select
                                    value={source}
                                    onChange={(e) => setSource(e.target.value)}
                                    data-testid="custom-report-source"
                                    className={inputCls}
                                >
                                    {catalog.map((s) => (
                                        <option key={s.key} value={s.key}>{s.label}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Window">
                                <select
                                    value={windowDays}
                                    onChange={(e) => setWindowDays(Number(e.target.value))}
                                    data-testid="custom-report-window"
                                    className={inputCls}
                                >
                                    {[7, 14, 30, 60, 90, 180, 365, 730].map((d) => (
                                        <option key={d} value={d}>{d <= 60 ? `Last ${d} days` : `Last ${Math.round(d / 30)} months`}</option>
                                    ))}
                                </select>
                            </Field>
                        </div>
                    </Section>

                    {/* Filters */}
                    <Section icon={Filter} title="Filters" right={
                        Object.keys(filters).length > 0 && (
                            <button
                                onClick={() => setFilters({})}
                                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                                data-testid="custom-report-filters-clear"
                            >
                                <FilterX size={12} /> Clear
                            </button>
                        )
                    }>
                        {currentSource.filter_fields.length === 0 ? (
                            <div className="text-xs text-muted-foreground">
                                This data source has no filterable fields. Use the time window above.
                            </div>
                        ) : (
                            <div className="grid gap-3 sm:grid-cols-2">
                                {currentSource.filter_fields.map((f) => (
                                    <Field key={f} label={currentSource.columns[f] || f}>
                                        <input
                                            value={filters[f] || ""}
                                            onChange={(e) => setFilters({ ...filters, [f]: e.target.value })}
                                            placeholder="any"
                                            data-testid={`custom-report-filter-${f}`}
                                            className={inputCls}
                                        />
                                    </Field>
                                ))}
                            </div>
                        )}
                    </Section>

                    {/* Columns */}
                    <Section icon={Columns3} title={`Columns (${columns.length} of ${Object.keys(currentSource.columns).length})`} right={
                        <div className="flex items-center gap-3 text-[11px]">
                            <button
                                onClick={() => setColumns(Object.keys(currentSource.columns))}
                                className="text-muted-foreground hover:text-foreground"
                                data-testid="custom-report-cols-all"
                            >
                                Select all
                            </button>
                            <button
                                onClick={() => setColumns(currentSource.default_columns)}
                                className="text-muted-foreground hover:text-foreground"
                                data-testid="custom-report-cols-default"
                            >
                                Reset to default
                            </button>
                        </div>
                    }>
                        <div className="grid gap-1.5 sm:grid-cols-2 md:grid-cols-3">
                            {Object.entries(currentSource.columns).map(([key, label]) => {
                                const checked = columns.includes(key);
                                return (
                                    <label
                                        key={key}
                                        className={[
                                            "flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs transition",
                                            checked
                                                ? "border-[#5b5ef0]/40 bg-[#5b5ef0]/5 text-foreground"
                                                : "border-border bg-background text-muted-foreground hover:border-foreground/30",
                                        ].join(" ")}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleColumn(key)}
                                            data-testid={`custom-report-col-${key}`}
                                            className="h-3.5 w-3.5 accent-[#5b5ef0]"
                                        />
                                        <span className="flex-1 truncate">{label}</span>
                                        <code className="font-mono text-[10px] opacity-60">{key}</code>
                                    </label>
                                );
                            })}
                        </div>
                    </Section>

                    {err && (
                        <div className="rounded-md border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5 px-3 py-2 text-xs text-[hsl(var(--destructive))]">
                            {err}
                        </div>
                    )}
                </div>

                <div className="sticky bottom-0 flex justify-end gap-2 border-t border-border bg-surface px-6 py-3">
                    <button
                        onClick={onClose}
                        className="rounded-md border border-border bg-background px-4 py-2 text-xs font-semibold uppercase tracking-widest hover:border-foreground/40"
                        data-testid="custom-report-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={save}
                        disabled={saving || columns.length === 0}
                        className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-[#5b5ef0] to-[#7c5cff] px-5 py-2 text-sm font-semibold text-white shadow hover:brightness-110 disabled:opacity-50"
                        data-testid="custom-report-save"
                    >
                        <Save size={13} />
                        {saving ? "Saving…" : initial ? "Save changes" : "Create report"}
                    </button>
                </div>
            </div>
        </Backdrop>
    );
}

function Backdrop({ children, onClose }) {
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            onClick={onClose}
            data-testid="custom-report-backdrop"
        >
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl">
                {children}
            </div>
        </div>
    );
}

function Section({ icon: Icon, title, right, children }) {
    return (
        <div className="rounded-lg border border-border bg-background p-4">
            <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                    <Icon size={12} /> {title}
                </div>
                {right}
            </div>
            {children}
        </div>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {label}
            </span>
            {children}
        </label>
    );
}

const inputCls =
    "w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-[#5b5ef0] focus:outline-none focus:ring-1 focus:ring-[#5b5ef0]/30";
